/**
 * payment webview의 axios 클라이언트 (onl-api).
 *
 * baseURL = API_BASE_URL (onl-api 호스트). order·payment 는 같은 호스트라
 * 호출 path의 prefix(API_PREFIX.ORDER / API_PREFIX.PAYMENT)로 구분한다.
 *
 * 인증: tokenStore의 token을 `Authorization: Bearer {token}` 헤더로 자동 주입.
 *
 * 응답 envelope:
 *   { txid, status, message, data, error? }
 *   - status < 400: data unwrap → 호출 측 반환
 *   - status >= 400: ApiError throw
 *
 * mobile-api는 envelope이 달라 별도 클라이언트(apis/mobileApi.ts)를 사용한다.
 *
 * 로깅: dev 환경 검증 편의를 위해 request/response/error 를 apiLog store 에 push.
 * DevScenarioPanel 이 구독해 화면에 표시.
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'

import { API_BASE_URL, API_TIMEOUT_MS } from '@/constants'
import { pushApiLog } from '@/lib/apiLog'

import { getToken, refreshTokenFromBridge } from './tokenStore'

interface RequestWithMeta extends InternalAxiosRequestConfig {
  metadata?: { startedAt: number }
  /** 401 자동 재시도 1회 플래그 — 무한 루프 방지 */
  _tokenRetried?: boolean
}

/** 토큰 만료 응답 코드 — envelope.status 또는 HTTP status 둘 다 401 케이스 */
const TOKEN_EXPIRED_STATUS = 401

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
})

export interface HttpClient extends AxiosInstance {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  post<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  put<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  patch<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
}

const api: HttpClient = axiosInstance

api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  ;(config as RequestWithMeta).metadata = { startedAt: Date.now() }
  pushApiLog({
    kind: 'request',
    method: (config.method ?? 'get').toUpperCase(),
    url: buildLogUrl(config),
    body: stringifyForLog(config.data),
    reqHeaders: summarizeHeaders(config.headers),
  })
  return config
})

interface ApiResponseEnvelope<T = unknown> {
  txid?: string
  status?: number
  message?: string
  data?: T
  error?: { code?: string; description?: string | null }
}

export class ApiError extends Error {
  code: string
  description: string | null
  status: number
  txid: string

  constructor(code: string, description: string | null, message: string | undefined, status: number, txid: string) {
    super(message ?? code)
    this.name = 'ApiError'
    this.code = code
    this.description = description
    this.status = status
    this.txid = txid
  }
}

api.interceptors.response.use(
  async (response) => {
    const body = response.data as ApiResponseEnvelope<unknown>
    const reqConfig = response.config as RequestWithMeta
    pushApiLog({
      kind: 'response',
      method: (response.config.method ?? 'get').toUpperCase(),
      url: buildLogUrl(response.config),
      status: response.status,
      durationMs: reqConfig.metadata ? Date.now() - reqConfig.metadata.startedAt : undefined,
      responseBody: stringifyForLog(response.data),
    })
    // 401 (토큰 만료) → 브릿지에서 토큰 갱신 후 원본 요청 1회 재시도
    if (body.status === TOKEN_EXPIRED_STATUS && !reqConfig._tokenRetried) {
      reqConfig._tokenRetried = true
      const refreshed = await refreshTokenFromBridge()
      if (refreshed) return api.request(reqConfig)
    }
    if (typeof body.status === 'number' && body.status >= 400) {
      throw new ApiError(
        body.error?.code ?? '',
        body.error?.description ?? null,
        body.message,
        body.status,
        body.txid ?? ''
      )
    }
    return body.data as never
  },
  async (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as ApiResponseEnvelope | undefined
      const reqConfig = error.config as RequestWithMeta | undefined
      pushApiLog({
        kind: 'error',
        method: (error.config?.method ?? 'get').toUpperCase(),
        url: error.config ? buildLogUrl(error.config) : '(unknown)',
        status: error.response?.status,
        durationMs: reqConfig?.metadata ? Date.now() - reqConfig.metadata.startedAt : undefined,
        message: body?.error?.code ?? body?.message ?? error.message,
        body: stringifyForLog(error.config?.data),
        responseBody: stringifyForLog(error.response?.data),
      })
      // HTTP 401 (envelope 인식 못한 케이스) → 동일하게 토큰 갱신 + 1회 재시도
      if (error.response?.status === TOKEN_EXPIRED_STATUS && reqConfig && !reqConfig._tokenRetried) {
        reqConfig._tokenRetried = true
        const refreshed = await refreshTokenFromBridge()
        if (refreshed) return api.request(reqConfig)
      }
      if (error.response && body) {
        return Promise.reject(
          new ApiError(
            body.error?.code ?? '',
            body.error?.description ?? null,
            body.message ?? error.message,
            body.status ?? error.response.status,
            body.txid ?? ''
          )
        )
      }
    }
    return Promise.reject(error)
  }
)

function summarizeHeaders(headers: unknown): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined
  const obj = headers as Record<string, unknown>
  const out: Record<string, string> = {}
  for (const key of Object.keys(obj)) {
    const v = obj[key]
    if (v == null) continue
    if (typeof v === 'function') continue
    const lower = key.toLowerCase()
    // Authorization 은 prefix 12자만 (전체 노출 방지)
    if (lower === 'authorization' && typeof v === 'string') {
      out[key] = `${v.slice(0, 18)}…`
      continue
    }
    out[key] = String(v)
  }
  try {
    return JSON.stringify(out)
  } catch {
    return undefined
  }
}

function stringifyForLog(data: unknown): string | undefined {
  if (data == null) return undefined
  if (typeof data === 'string') return data
  try {
    return JSON.stringify(data)
  } catch {
    return undefined
  }
}

function buildLogUrl(config: AxiosRequestConfig): string {
  const base = typeof config.url === 'string' ? config.url : ''
  if (!config.params || typeof config.params !== 'object') return base
  const query = new URLSearchParams(
    Object.entries(config.params).reduce<Record<string, string>>((acc, [k, v]) => {
      if (v == null) return acc
      acc[k] = String(v)
      return acc
    }, {})
  ).toString()
  return query ? `${base}?${query}` : base
}

export default api
